// GENERATED FILE — do not edit. Edit packages/plugin-ui-kit/src/resourceHistory.js (chart rules) or packages/plugin-shared/resourceHistory.mjs (vocabulary) and run `node scripts/generate-resource-history.mjs`.
// tests/contract/resourceHistoryVocabulary.test.ts fails when this file and its sources disagree.

// packages/plugin-shared/resourceHistory.mjs
var MINUTE = 6e4;
var HOUR = 60 * MINUTE;
var RESOURCE_METRICS = (
  /** @type {const} */
  {
    cpu: ["cpu"],
    memory: ["memory"],
    disk: ["disk"],
    io: ["ioRead", "ioWrite"],
    network: ["netRx", "netTx"]
  }
);
var RESOURCE_SERIES = (
  /** @type {const} */
  {
    cpu: { source: "cpu", value: "usedCpus", limit: null, unit: "cores" },
    memory: { source: "memory", value: "usedBytes", limit: "limitBytes", unit: "bytes" },
    disk: { source: "disk", value: "usedBytes", limit: "limitBytes", unit: "bytes" },
    ioRead: { source: "io", value: "readBytesPerSec", limit: "limitBytesPerSec", unit: "bytes/s" },
    ioWrite: { source: "io", value: "writeBytesPerSec", limit: "limitBytesPerSec", unit: "bytes/s" },
    netRx: { source: "network", value: "rxBytesPerSec", limit: "limitBytesPerSec", unit: "bytes/s" },
    netTx: { source: "network", value: "txBytesPerSec", limit: "limitBytesPerSec", unit: "bytes/s" }
  }
);
var RESOURCE_METRIC_IDS = (
  /** @type {Array<keyof typeof RESOURCE_METRICS>} */
  /* @__PURE__ */ Object.keys(RESOURCE_METRICS)
);
var RESOURCE_SERIES_IDS = (
  /** @type {Array<keyof typeof RESOURCE_SERIES>} */
  /* @__PURE__ */ Object.keys(RESOURCE_SERIES)
);
var RESOURCE_SERIES_UNITS = [...new Set(Object.values(RESOURCE_SERIES).map((series) => series.unit))];
var resourceHistorySeriesIds = (metrics) => metrics.flatMap((metric) => (
  /** @type {Array<keyof typeof RESOURCE_SERIES>} */
  [...RESOURCE_METRICS[metric]]
));
var WINDOWS = Object.freeze({ "1h": HOUR, "6h": 6 * HOUR, "24h": 24 * HOUR, "7d": 7 * 24 * HOUR, "30d": 30 * 24 * HOUR });
var RESOLUTION_SECONDS = Object.freeze({ "1m": 60, "5m": 300, "1h": 3600 });
var RESOURCE_HISTORY = Object.freeze({
  MINUTE,
  HOUR,
  WINDOWS,
  /** The fixed bucket sizes a client may request instead of `auto`, in seconds as the response states them. */
  RESOLUTION_SECONDS,
  DETAIL: 48 * HOUR,
  RETENTION: 30 * 24 * HOUR,
  DETAIL_HOURS: 48,
  RETENTION_DAYS: 30,
  MAX_BUCKETS: 720
});
var RESOURCE_POINT_STATES = (
  /** @type {const} */
  ["ready", "sampling", "stopped", "unavailable", "missing"]
);

// packages/plugin-ui-kit/src/resourceHistory.js
function validResourceHistory(data, metrics) {
  const ids = resourceHistorySeriesIds(metrics).sort().join(",");
  if (data.series.map((series) => series.id).sort().join(",") !== ids || Date.parse(data.range.from) > Date.parse(data.range.to)) return false;
  return !data.series.some((series) => {
    let previous = -Infinity;
    return series.points.some((point) => {
      const at = Date.parse(point.at);
      const invalid = at <= previous || Date.parse(point.until) <= at || point.validSamples > point.expectedSamples;
      previous = at;
      return invalid;
    });
  });
}
var METRIC_KINDS = RESOURCE_METRIC_IDS;
var metricLabel = (kind, s) => kind === "cpu" ? s.usageCpu : kind === "memory" ? s.usageRam : kind === "disk" ? s.usageDisk : kind === "io" ? s.usageIo : s.usageNetwork;
var METRIC_COLOUR = {
  cpu: "var(--color-chart-1)",
  memory: "var(--color-chart-4)",
  disk: "var(--color-chart-6)",
  io: "var(--color-chart-5)",
  network: "var(--color-chart-2)"
};
var historyTime = (at, locale) => new Intl.DateTimeFormat(locale, { dateStyle: "short", timeStyle: "long" }).format(at);
function ioFigure(bytesPerSec, locale) {
  const megabytes = bytesPerSec / 1e6;
  const digits = megabytes < 9.995 ? 2 : megabytes < 99.95 ? 1 : 0;
  return new Intl.NumberFormat(locale, { useGrouping: false, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(megabytes);
}
var networkFigure = (bytesPerSec, locale) => ioFigure(bytesPerSec * 8, locale);
function resourceHistoryChart(data, metrics, s, locale, formatBytes) {
  const selected = METRIC_KINDS.filter((metric) => metrics.includes(metric));
  const percent = (value) => new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value) + "%";
  const rate = (value) => new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value) + " MB/s";
  const rateMegabits = (value) => new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value) + " Mb/s";
  const RATE_METRICS = /* @__PURE__ */ new Set(["io", "network"]);
  const ceilingless = new Map(selected.map((metric) => [metric, RATE_METRICS.has(metric) && !data.series.some((series) => RESOURCE_METRICS[metric].includes(series.id) && series.points.some((point) => point.percentMean !== null))]));
  const asRate = (metric) => metric === "network" || ceilingless.get(metric) === true;
  const rateOf = (metric, mean) => metric === "network" ? mean * 8 / 1e6 : mean / 1e6;
  const pointTime = (at) => new Intl.DateTimeFormat(locale, { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" }).format(at);
  const points = /* @__PURE__ */ new Map();
  const put = (at, metric, point) => {
    const row = points.get(at) ?? { label: pointTime(at), at };
    const share = point?.percentMean ?? null;
    const mean = point?.mean ?? null;
    const current = row[metric + "Percent"];
    const currentMean = row[metric + "Mean"];
    const keep = typeof current === "number" && (share === null || current > share) || current == null && share === null && typeof currentMean === "number" && (mean === null || currentMean > mean);
    if (!keep) {
      row[metric] = asRate(metric) ? mean === null ? null : rateOf(metric, mean) : share;
      row[metric + "Mean"] = mean;
      row[metric + "Percent"] = share;
    }
    points.set(at, row);
  };
  for (const metric of selected) {
    const sources = data.series.filter((series) => RESOURCE_METRICS[metric].includes(series.id));
    for (const series of sources) {
      let previous;
      for (const point of series.points) {
        const at = Date.parse(point.at);
        if (previous) {
          const gap = Date.parse(previous.until) < at;
          const generationChanged = previous.generation !== null && point.generation !== null && previous.generation !== point.generation;
          if (gap || generationChanged) put(gap ? Date.parse(previous.until) : (Date.parse(previous.at) + at) / 2, metric, null);
        }
        put(at, metric, point.discontinuous || point.state !== "ready" ? null : point);
        previous = point;
      }
    }
  }
  const rows = [...points].sort(([a], [b]) => a - b).map(([, row]) => {
    for (const metric of selected) if (!(metric in row)) row[metric] = null;
    return row;
  });
  const from = Date.parse(data.range.from);
  const to = Date.parse(data.range.to);
  return {
    data: rows,
    series: selected.map((metric) => ({
      key: metric,
      label: metricLabel(metric, s),
      colour: METRIC_COLOUR[metric],
      variant: "line",
      axis: asRate(metric) ? "right" : "left",
      format: metric === "network" ? rateMegabits : asRate(metric) ? rate : percent,
      ...metric === "cpu" ? {} : {
        formatPoint: (point) => {
          const mean = point[metric + "Mean"];
          if (typeof mean !== "number") return null;
          const value = metric === "network" ? networkFigure(mean, locale) + " Mb/s" : metric === "io" ? ioFigure(mean, locale) + " MB/s" : formatBytes(mean, locale);
          const share = point[metric + "Percent"];
          return typeof share === "number" ? value + " · " + new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(share) + " %" : value;
        }
      }
    })),
    // No ariaLabel: the surface prints the visible caption right above the figure, and a hidden copy of it
    // would be read twice.
    height: "fill",
    timeAxis: {
      dataKey: "at",
      domain: [from, to],
      tickFormat: (at) => new Intl.DateTimeFormat(locale, { ...to - from > RESOURCE_HISTORY.HOUR * 24 ? { month: "numeric", day: "numeric" } : {}, hour: "2-digit", minute: "2-digit" }).format(at),
      tooltipFormat: pointTime
    }
  };
}
export {
  METRIC_COLOUR,
  METRIC_KINDS,
  RESOURCE_HISTORY,
  RESOURCE_METRICS,
  RESOURCE_POINT_STATES,
  RESOURCE_SERIES_IDS,
  RESOURCE_SERIES_UNITS,
  historyTime,
  ioFigure,
  metricLabel,
  networkFigure,
  resourceHistoryChart,
  resourceHistorySeriesIds,
  validResourceHistory
};
